"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import DoseComponents from "@/components/dose/dose-form/dose-components";
import { DoseEventFields, DoseProviderField } from "@/components/dose/dose-form/dose-event-fields";
import { DoseFormValues, doseFormSchema } from "@/components/dose/dose-form/dose-form-schema";
import { findMedicationForEditComponent, getEditDoseFormValues, toEditDosePayload } from "@/components/dose/dose-form/edit-dose-model";
import { useDoseComponentInventory } from "@/components/dose/dose-form/use-dose-component-inventory";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { hasChartLocations } from "@/lib/dose-locations";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { getDoseCommentLabel } from "@/lib/tenant-flags";
import { Medication, MedicationSummary } from "@/types/medication";
import { assertDoseLotResolution } from "./dose-form/dose-lot-resolution";
import { useUpdateDoseManagementStatusMutation, useUpdateMedicationMutation } from "@/api/provider/user/patient/patient-mutation";
import { medicationsQueryOptions } from "@/query/medications/medications";
interface EditDoseProps {
  medication: MedicationSummary;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
const EMPTY_MEDICATIONS: Medication[] = [];
const FORM_ID = "edit-dose-form";
export default function EditDose({
  medication,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange
}: EditDoseProps) {
  const updateMedicationMutation = useUpdateMedicationMutation();
  const updateDoseManagementStatusMutation = useUpdateDoseManagementStatusMutation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = controlledOnOpenChange ?? setUncontrolledOpen;
  const {
    currentUser,
    currentLocationId
  } = useUser();
  const {
    tenant
  } = useTenant();
  const {
    data: allMedications = EMPTY_MEDICATIONS,
    isPending
  } = useQuery(medicationsQueryOptions.list());
  const canChangeProvider = hasPermission(currentUser, PERMISSIONS.PATIENTS_DOSES_PROVIDER_WRITE);
  const form = useForm<DoseFormValues>({
    resolver: zodResolver(doseFormSchema),
    defaultValues: getEditDoseFormValues(medication),
    mode: "onSubmit"
  });
  const components = useWatch({
    control: form.control,
    name: "components"
  });
  // Scope lots to the administering provider (may be changed via DoseProviderField).
  const administeringProviderId = useWatch({
    control: form.control,
    name: "providerId"
  });
  const {
    dirtyFields
  } = form.formState;
  // The location is edited as a list of places and amounts only where that is
  // in use: the clinic has the setting on and either this provider records
  // doses by muscle or the dose already has locations. Otherwise it is the
  // single text box, as it always was.
  const locationList = tenant?.faceChartMuscleDosesEnabled === true && (currentUser?.faceChartMuscles === true || (medication.locations?.length ?? 0) > 0);
  const isStatusOnlyUpdate = !!dirtyFields.managementStatus && Object.keys(dirtyFields).every(field => field === "managementStatus");
  const isMutationPending = updateMedicationMutation.isPending || updateDoseManagementStatusMutation.isPending;
  const compoundMedicationId = useWatch({
    control: form.control,
    name: "compoundMedicationId"
  });
  const inventory = useDoseComponentInventory({
    components,
    compoundMedicationId: compoundMedicationId ?? null,
    medications: allMedications,
    resolveMedication: findMedicationForEditComponent,
    // An edit redraws at the dose's own location, so list that location's lots.
    currentLocationId: medication.locationId ?? currentLocationId,
    providerId: administeringProviderId || currentUser?.id,
    patientId: medication.patientId,
    enabled: open,
    isPendingMedications: isPending
  });
  const isInventoryCheckPending = inventory.isCheckingInventory && !isStatusOnlyUpdate;
  const previousOpenRef = useRef(open);
  const previousMedicationIdRef = useRef(medication.id);
  useEffect(() => {
    const justOpened = open && !previousOpenRef.current;
    const medicationChanged = medication.id !== previousMedicationIdRef.current;
    previousOpenRef.current = open;
    previousMedicationIdRef.current = medication.id;
    if (open && (justOpened || medicationChanged)) {
      form.reset(getEditDoseFormValues(medication));
    }
  }, [form, medication, open]);
  const handleProviderChange = useCallback(() => {
    components.forEach((component, index) => {
      if (!component.lotSelection) return;
      form.setValue(`components.${index}.lotSelection`, null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
    });
  }, [components, form]);
  const handleFormSubmit = async (values: DoseFormValues) => {
    if (!currentUser || isInventoryCheckPending || isMutationPending) {
      return;
    }
    if (!isStatusOnlyUpdate) for (const [index_0, component_0] of values.components.entries()) {
      try {
        assertDoseLotResolution(component_0, inventory.entries[index_0] ?? {
          shipments: []
        });
      } catch (error) {
        form.setError(`components.${index_0}.lotSelection`, {
          message: error instanceof Error ? error.message : "Resolve this lot before saving."
        });
        return;
      }
    }
    try {
      if (isStatusOnlyUpdate) {
        await updateDoseManagementStatusMutation.mutateAsync({
          doseId: medication.id,
          managementStatus: values.managementStatus
        });
      } else {
        await updateMedicationMutation.mutateAsync({
          medicationId: medication.id,
          update: toEditDosePayload(values, {
            medication,
            medications: allMedications,
            inventory: inventory.entries.map(entry => ({
              ...entry,
              medicationId: entry.selectedMedication?.id
            })),
            canChangeProvider,
            editLocations: locationList
          })
        });
      }
      setOpen(false);
    } catch {
      // Optimistic update, rollback, and error toast handled by the mutation.
    }
  };
  return <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <FormProvider {...form}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Dose</DialogTitle>
            <DialogDescription>
              Update the shared dose event details and component medications for
              this patient.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <form id={FORM_ID} onSubmit={event => {
            if (isStatusOnlyUpdate) {
              // The status-only endpoint never reconciles inventory; an
              // unavailable historical receipt must not block this action.
              event.preventDefault();
              void handleFormSubmit(form.getValues());
            } else {
              void form.handleSubmit(handleFormSubmit)(event);
            }
          }} className="space-y-4">
              {canChangeProvider && <DoseProviderField onValueChange={handleProviderChange} />}
              <DoseComponents inventory={inventory.entries} medications={allMedications} />
              <DoseEventFields commentLabel={getDoseCommentLabel(tenant)} locationList={locationList} locationsFromChart={hasChartLocations(medication)} />
            </form>
          </DialogBody>

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form={FORM_ID} disabled={components.length === 0} isLoading={isMutationPending || isInventoryCheckPending} loadingText={isInventoryCheckPending ? "Checking inventory..." : "Saving..."}>
              Update Dose
            </Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </Dialog>;
}